import React from 'react';
import { getZonesOfGrowthRanges } from '@amplify/struggling-advanced-goal';
import head from 'lodash.head';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {
    DEFAULT_INTERPS,
    INTERP_COLOR_MAPPER,
} from '../../constants/shared/CommonInterpretationMapper';
import { MEASURE } from '../../constants/shared/MeasuresConstants';
import {
    KEY,
    TITLE as PM_TITLE,
    PM_COL_SPAN_SUBHEADER_CELL,
} from '../../constants/shared/PMConstants';
import { MAX_RANGE, ZOG_SECTIONS, INDEX as ZOG_INDEX } from '../../constants/shared/ZoGConstants';
import {
    buildRunningGirlLevels,
    getCorrectCutScores,
} from '../../features/probeDetails/shared/runningGirlHelper';
import {
    buildDividedColSpan,
    getBenchmarkScoreCell,
    getCommonGradeHeaderCell,
    getDividedColSpan,
    getProgressGridHeader,
    getReverseThresholds,
    getStudentNameCommonCell,
    getTwoPartHeaderCell,
} from '../../features/progress/shared/progressHelper';
import {
    getGoalByMeasureYearGradePeriod,
    getStudentResultByMeasureYearGradePeriod,
    getStudentResultsByMeasureYearGradePeriod,
} from './filterUtils';
import { isMeasureAvailableInGrade } from './gradeToMeasureUtils';
import { getEndPmPeriod, getStartPmPeriod } from './pmUtils';
import { TextCell, ZogRangeCell } from '../../components/cell/shared/Cells';
import ZonesOfGrowthGoalCell from '../../features/progress/shared/zonesOfGrowth/ZonesOfGrowthGoalCell';
import { INDEX as CELL_INDEX, CLASS_NAME } from '../../constants/shared/CellConstants';
import { sortByPredefinedOrder } from './sortUtils';
import { LECTURA_ZOG_MEASURES } from '../../constants/espanol/MeasuresConstants';

const { OVERALL, ORF_ACC, FLO_PREC } = MEASURE;
const { SKILL_KEY, PERIOD_KEY } = KEY;
const { FIRST_ARRAY_INDEX, FIRST_GRID_ROW, START_FIRST_COL_SPAN_INDEX } = CELL_INDEX;
const { LAST_SCORES_START_INDEX, LAST_SCORES_END_INDEX, COUNT_OF_COLUMNS } = ZOG_INDEX;
const { SKILL_TITLE } = PM_TITLE;

const readingAccuracyMeasures = [ORF_ACC, FLO_PREC];

export const getZoGGridData = ({
    statuses,
    growthZones,
    interps,
    studentGoals,
    compositeGoal,
    year,
    grade,
    pmPeriod,
    getGradeToMeasureMapper,
    getMeasureByGrade,
}) => {
    const startPeriod = getStartPmPeriod(pmPeriod);
    const endPeriod = getEndPmPeriod(pmPeriod);
    const pmStatusesData = getMeasureByGrade(grade, true, getGradeToMeasureMapper).map(measure => {
        const zogStatuses = getStudentResultByMeasureYearGradePeriod(
            statuses,
            measure,
            year,
            grade,
            startPeriod,
        );

        const interpsByMeasureGrade = getStudentResultsByMeasureYearGradePeriod(
            interps,
            measure,
            null,
            grade,
        );

        const zones = getStudentResultByMeasureYearGradePeriod(
            growthZones,
            measure,
            year,
            grade,
            startPeriod,
        );

        return {
            measure,
            interpsByMeasureGrade,
            thresholds: getReverseThresholds(interps, measure, grade, endPeriod),
            zogStatuses,
            growthZones: zones,
            customGoal:
                measure === OVERALL
                    ? compositeGoal
                    : getGoalByMeasureYearGradePeriod(
                          studentGoals,
                          measure,
                          year,
                          grade,
                          endPeriod,
                      ),
        };
    });

    return sortByPredefinedOrder(
        pmStatusesData.concat(pmStatusesData.splice(0, 1)),
        LECTURA_ZOG_MEASURES,
        'measure',
    );
};

export const getLastCutScoreByGoal = (goal, cutScores) =>
    cutScores.filter(cutScore => cutScore !== null && cutScore <= goal).pop() || 0;

export const getDefaultRange = (goal, range, isWAA) => {
    if (isEmpty(range)) return '';
    const splitter = isWAA ? ' ' : '-';
    const rangeList = range.split(splitter).map(rangeString => Number(rangeString.trim()));
    if (isWAA) {
        return head(rangeList) <= goal ? range : '';
    }
    return head(rangeList) <= goal && goal <= rangeList.pop() ? range : '';
};

export const getPlusSign = (measure, rangeValue) =>
    (readingAccuracyMeasures.includes(measure) && rangeValue === '100') || isEmpty(rangeValue)
        ? ''
        : '+';

const isGrayBgClass = measure => (measure === OVERALL ? 'gray-bg' : '');

export const getGrowthRateClass = (measure, customGoal, range, isWAA) => {
    return `${PM_COL_SPAN_SUBHEADER_CELL} growth-rate-cell ${isGrayBgClass(measure)} ${
        measure === OVERALL && !isEmpty(getDefaultRange(customGoal, range, isWAA))
            ? 'with-selected-mark'
            : ''
    }`;
};

const getRangeAsString = range => {
    if (isEmpty(range)) {
        return '';
    }
    const { min, max } = range;
    if (min === max) return `${min}`;
    const maxFormatted = isNil(max) ? '' : ` - ${max}`;
    return `${min}${maxFormatted}`;
};

const doesRangeConsistOfOneValue = ({ min, max }) => min >= max || isNil(max);

const isRangeBoundariesEqual = (range1, range2) =>
    !isEmpty(range1) &&
    !isEmpty(range2) &&
    range1.min === range2.min &&
    doesRangeConsistOfOneValue(range1) &&
    doesRangeConsistOfOneValue(range2);

const isMetReadingAccuracyRule = (measure, aboveAverageRange, wellAboveAverageRange) =>
    readingAccuracyMeasures.includes(measure) &&
    wellAboveAverageRange.min === 100 &&
    (isEmpty(aboveAverageRange) || aboveAverageRange.max === 100);

const isOverlappedWith = (range1, range2) =>
    range2 && (isEmpty(range1) || range1.max === range2.min);

/**
 * Check when some additional rules (like "ORF_ACC",  or "include 100 value" cases) should be applied.
 * To apply these rules we should have at least two adjacent ranges.
 * @param {*} rangesObj - consists of averageRange, aboveAverageRange, wellAboveAverageRange
 * @return boolean - whether we should apply additional rules or not
 */

const shouldApplyAdditionalRulesToRanges = ({
    averageRange,
    aboveAverageRange,
    wellAboveAverageRange,
}) =>
    isRangeBoundariesEqual(averageRange, aboveAverageRange) ||
    isOverlappedWith(aboveAverageRange, wellAboveAverageRange);

/**
 * readingAccuracyMeasures = [ORF_ACC, FLO_PREC]
 * Applies some additional rules (like "ORF_ACC || FLO_PREC + include 100 value" cases) to ranges.
 * Taking into account measure we consider the following cases:
 *  - averageRange, aboveAverageRange, wellAboveAverageRange are equal. When aboveAverageRange is equal to wellAboveAverageRange
 *    getZonesOfGrowthRanges returns rangesObj with empty aboveAverageRange.
 *    So we need to check equality between averageRange and wellAboveAverageRange,
 *    Example:
 *    -------------------------------------------------------
 *    |    Ranges   |  Other measures  | ORF Acc || FLO_PREC |
 *    -------------------------------------------------------
 *    |  99  99  99 | empty  empty  99 | empty   empty   99 |
 *    -------------------------------------------------------
 *    | 100 100 100 | 100 empty 100    | 100  empty   empty |
 *    -------------------------------------------------------
 *  - only averageRange and aboveAverageRange are equal.
 *    We should return averageRange and wellAboveAverageRange depending on readingAccuracyMeasures rule.
 *    Example:
 *    -------------------------------------------------------
 *    |    Ranges   |  Other measures  | ORF Acc || FLO_PREC |
 *    -------------------------------------------------------
 *    | 99  99  100 | 99  empty 100    | 99   empty   100   |
 *    -------------------------------------------------------
 *  - only aboveAverageRange and wellAboveAverageRange are equal.
 *     Here we need to check the presence of wellAboveAverageRange and emptiness of aboveAverageRange.
 *     We should return averageRange and both of aboveAverageRange, wellAboveAverageRange depending on readingAccuracyMeasures rule.
 *    Example:
 *    -------------------------------------------------------
 *    |    Ranges   |  Other measures  | ORF Acc || FLO_PREC |
 *    -------------------------------------------------------
 *    |  98  99  99 | 98  empty  99    | 98   empty   99    |
 *    -------------------------------------------------------
 *    |  99 100 100 | 99  empty 100    | 99   100   empty   |
 *    -------------------------------------------------------
 * @param {*} rangesObj - consists of averageRange, aboveAverageRange, wellAboveAverageRange.
 * @param {*} measure - D8 or Lectura measure
 * @returns ranges as strings
 */

const applyAdditionalRulesToRanges = (
    { averageRange, aboveAverageRange, wellAboveAverageRange },
    measure,
) => {
    const metReadingAccuracyRule = isMetReadingAccuracyRule(
        measure,
        aboveAverageRange,
        wellAboveAverageRange,
    );
    const ranges = {
        averageRangeStr: getRangeAsString(averageRange),
        aboveAverageRangeStr: '',
        wellAboveAverageRangeStr: metReadingAccuracyRule
            ? ''
            : getRangeAsString(wellAboveAverageRange),
    };
    if (isRangeBoundariesEqual(averageRange, wellAboveAverageRange)) {
        return {
            ...ranges,
            averageRangeStr: metReadingAccuracyRule ? getRangeAsString(averageRange) : '',
        };
    }
    if (isRangeBoundariesEqual(averageRange, aboveAverageRange)) {
        return ranges;
    }
    const range = isEmpty(aboveAverageRange) ? wellAboveAverageRange : aboveAverageRange;
    return {
        ...ranges,
        aboveAverageRangeStr:
            metReadingAccuracyRule && averageRange.max !== MAX_RANGE ? getRangeAsString(range) : '',
    };
};

export const getRangesAsStrings = (rangesObj, measure) => {
    if (shouldApplyAdditionalRulesToRanges(rangesObj)) {
        return applyAdditionalRulesToRanges(rangesObj, measure);
    }
    const { averageRange, aboveAverageRange, wellAboveAverageRange } = rangesObj;
    return {
        averageRangeStr: getRangeAsString(averageRange),
        aboveAverageRangeStr: getRangeAsString(aboveAverageRange),
        wellAboveAverageRangeStr: getRangeAsString(wellAboveAverageRange),
    };
};

export const getMediumRoundedValue = (scoreObject, selectedValue) => {
    if (isEmpty(scoreObject)) return Number(selectedValue);
    return Math.round((scoreObject.min + scoreObject.max) / 2);
};

const calculateMediumValue = (selectedValue, additionalInfo) => {
    const { averageRangeStr, aboveAverageRangeStr, averageRange, aboveAverageRange } =
        additionalInfo;

    if (selectedValue === averageRangeStr) {
        return getMediumRoundedValue(averageRange, selectedValue);
    }
    if (selectedValue === aboveAverageRangeStr) {
        return getMediumRoundedValue(aboveAverageRange, selectedValue);
    }
    return Number(selectedValue);
};

/**
 * Calculate and set medium value to input.
 * In case of quick selecting radio buttons, when ref.current equal null, go out from method.
 * @param {String} selectedValue Selected value from radio button
 * @param {Object} additionalInfo Additional info with ranges, statuses, ref etc.
 */
export const handleSelectedRadioButton = (selectedValue, additionalInfo) => {
    const { inputRef } = additionalInfo;
    if (isNil(inputRef.current)) return;

    inputRef.current.value = calculateMediumValue(selectedValue, additionalInfo);
    inputRef.current.focus();
    inputRef.current.blur();
};

const buildStudentRow = ({
    grade,
    studentInfo,
    location,
    selectedPmPeriod,
    dataByMeasure = {},
    studentId,
    handleSaveStudentGoal,
    goalSetLookerEvent,
    isDemoModeOn,
    gradeToMeasureMapper,
    appSpecificParams,
}) => {
    const {
        measure,
        interpsByMeasureGrade,
        zogStatuses,
        thresholds = [],
        growthZones = {},
        customGoal,
    } = dataByMeasure;
    const {
        getStrugglingAdvancedGoalForMeasure,
        dyslexiaMeasures = [],
        ssUrl,
        SkillCell,
        CommonCellBuilder,
        appName,
    } = appSpecificParams;

    const runningGirlLevels = buildRunningGirlLevels(
        thresholds.map(threshold => threshold.interp),
        !dyslexiaMeasures?.includes(measure) && DEFAULT_INTERPS,
    );
    const barColors = runningGirlLevels.map(level => INTERP_COLOR_MAPPER[level]);
    const cutScores = getCorrectCutScores(runningGirlLevels, thresholds);

    const { averageRange, aboveAverageRange, wellAboveAverageRange } = getZonesOfGrowthRanges(
        zogStatuses,
        growthZones.growthRates,
    );

    const { averageRangeStr, aboveAverageRangeStr, wellAboveAverageRangeStr } = getRangesAsStrings(
        { averageRange, aboveAverageRange, wellAboveAverageRange },
        measure,
    );
    const isOverallNotFinished = dataByMeasure && !isNil(zogStatuses) && isNil(zogStatuses.score);
    const goal = getLastCutScoreByGoal(customGoal, cutScores);
    const benchmarkScoreParams = {
        externalId: studentId,
        measure,
        grade,
        location,
        period: getStartPmPeriod(selectedPmPeriod),
        statusForMeasureYearPeriod: zogStatuses,
        optionalCellData: {
            classNames: isGrayBgClass(measure),
        },
        isCellDisabled: !isMeasureAvailableInGrade(gradeToMeasureMapper, grade, measure),
    };
    const studentSaveInfo = {
        studentId,
        measure,
        period: getEndPmPeriod(selectedPmPeriod),
        grade,
    };
    const inputRef = React.createRef();
    const additionalInfo = {
        customGoal,
        averageRangeStr,
        aboveAverageRangeStr,
        averageRange,
        aboveAverageRange,
        selectedPmPeriod,
        zogStatuses,
        interpsByMeasureGrade,
        growthZones,
        handleSaveStudentGoal,
        goalSetLookerEvent,
        inputRef,
    };

    const defineGrowthRateDefaultValue = {
        average: getDefaultRange(customGoal, averageRangeStr, false),
        'above-average': getDefaultRange(customGoal, aboveAverageRangeStr, false),
        'well-above-average': getDefaultRange(customGoal, wellAboveAverageRangeStr, true),
    };

    let growthRatePrev = {};

    Object.keys(defineGrowthRateDefaultValue).forEach(key => {
        if (defineGrowthRateDefaultValue[key].length) {
            growthRatePrev = {
                prevGoalLevel: key,
                prevGoalRange: defineGrowthRateDefaultValue[key],
            };
        }
    });

    return {
        ...getStudentNameCommonCell(studentInfo, location, ssUrl.PROGRESS),
        skill: (
            <SkillCell
                measure={measure}
                showMeasureName={measure !== OVERALL}
                className={isGrayBgClass(measure)}
            />
        ),
        ...getBenchmarkScoreCell(
            PERIOD_KEY,
            benchmarkScoreParams,
            isEmpty(dataByMeasure),
            CommonCellBuilder,
            isGrayBgClass(measure),
        ),
        average: (
            <ZogRangeCell
                classNames={getGrowthRateClass(measure, customGoal, averageRangeStr, false)}
                defaultRange={getDefaultRange(customGoal, averageRangeStr, false)}
                range={averageRangeStr}
                measure={measure}
                studentId={studentId}
                isDemoModeOn={isDemoModeOn}
                onSelectHandler={selectedValue =>
                    handleSelectedRadioButton(selectedValue, additionalInfo)
                }
            />
        ),
        aboveAverage: (
            <ZogRangeCell
                classNames={getGrowthRateClass(measure, customGoal, aboveAverageRangeStr, false)}
                defaultRange={getDefaultRange(customGoal, aboveAverageRangeStr, false)}
                range={aboveAverageRangeStr}
                measure={measure}
                studentId={studentId}
                isDemoModeOn={isDemoModeOn}
                onSelectHandler={selectedValue =>
                    handleSelectedRadioButton(selectedValue, additionalInfo)
                }
            />
        ),

        wellAboveAverage: (
            <ZogRangeCell
                classNames={getGrowthRateClass(measure, customGoal, wellAboveAverageRangeStr, true)}
                defaultRange={getDefaultRange(customGoal, wellAboveAverageRangeStr, true)}
                range={wellAboveAverageRangeStr}
                measure={measure}
                studentId={studentId}
                isDemoModeOn={isDemoModeOn}
                rangeLabel={
                    wellAboveAverageRangeStr === aboveAverageRangeStr
                        ? ''
                        : `${wellAboveAverageRangeStr}${getPlusSign(
                              measure,
                              wellAboveAverageRangeStr,
                          )}`
                }
                onSelectHandler={selectedValue =>
                    handleSelectedRadioButton(selectedValue, additionalInfo)
                }
            />
        ),
        goal: (
            <ZonesOfGrowthGoalCell
                cutScores={cutScores}
                goal={goal}
                barColors={barColors}
                isDisabled={isDemoModeOn}
                isOverallNotFinished={isOverallNotFinished}
                studentSaveInfo={studentSaveInfo}
                additionalInfo={additionalInfo}
                growthRatePrev={growthRatePrev}
                getStrugglingAdvancedGoalForMeasure={getStrugglingAdvancedGoalForMeasure}
                appName={appName}
            />
        ),
    };
};

const getStudentPmRows = ({
    grade,
    studentData,
    location,
    selectedPmPeriod,
    handleSaveStudentGoal,
    goalSetLookerEvent,
    isDemoModeOn,
    gradeToMeasureMapper,
    appSpecificParams,
}) => {
    const { studentInfo, data } = studentData;

    if (isEmpty(data)) {
        return Array.of(
            buildStudentRow({
                grade,
                studentInfo,
                location,
                selectedPmPeriod,
                studentId: studentInfo.id,
                handleSaveStudentGoal,
                goalSetLookerEvent,
                isDemoModeOn,
                gradeToMeasureMapper,
                appSpecificParams,
            }),
        );
    }

    return data.map((dataByMeasure, index) => {
        const isFirstIndex = index === FIRST_ARRAY_INDEX;
        return buildStudentRow({
            grade,
            studentInfo: isFirstIndex ? studentInfo : {},
            location: isFirstIndex ? location : {},
            selectedPmPeriod,
            dataByMeasure,
            studentId: studentInfo.id,
            handleSaveStudentGoal,
            goalSetLookerEvent,
            isDemoModeOn,
            gradeToMeasureMapper,
            appSpecificParams,
        });
    });
};

const getHeaderRow = (currentPmPeriod, grade) => ({
    ...getCommonGradeHeaderCell(grade),
    ...getTwoPartHeaderCell(SKILL_KEY, CLASS_NAME.LEFT_ALIGN, '', SKILL_TITLE),
    period: (
        <TextCell
            classNames={PM_COL_SPAN_SUBHEADER_CELL}
            content={
                <>
                    <div className="start-period">{`Start - ${getStartPmPeriod(
                        currentPmPeriod,
                    )}`}</div>
                    <div className="header-score">Score</div>
                </>
            }
            isDataCell={false}
        />
    ),
    average: {},
    aboveAverage: {},
    wellAboveAverage: {},
    goal: (
        <TextCell
            classNames={PM_COL_SPAN_SUBHEADER_CELL}
            content={
                <>
                    <div className="end-period">{`End - ${getEndPmPeriod(currentPmPeriod)}`}</div>
                    <div className="header-goal">Goal Set</div>
                </>
            }
            isDataCell={false}
        />
    ),
});

const buildRowsForGrade = ({
    gradeResult,
    selectedPmPeriod,
    location,
    handleSaveStudentGoal,
    goalSetLookerEvent,
    isDemoModeOn,
    gradeToMeasureMapper,
    appSpecificParams,
}) => {
    const studentsRowObjects = [];
    const { grade, studentsData } = gradeResult;

    studentsData.forEach(studentData => {
        const studentRows = getStudentPmRows({
            grade,
            studentData,
            location,
            selectedPmPeriod,
            handleSaveStudentGoal,
            goalSetLookerEvent,
            isDemoModeOn,
            gradeToMeasureMapper,
            appSpecificParams,
        });
        const studentRowObjects = studentRows.map(studentRow => ({
            rowObject: studentRow,
        }));
        studentsRowObjects.push(studentRowObjects);
    });
    return [
        [
            {
                isSubheader: true,
                rowObject: getHeaderRow(selectedPmPeriod, grade),
            },
        ],

        ...studentsRowObjects,
    ];
};

export const getRowsToRender = ({
    resultsByGrades,
    selectedPmPeriod,
    location,
    handleSaveStudentGoal,
    goalSetLookerEvent,
    isDemoModeOn,
    gradeToMeasureMapper,
    appSpecificParams,
}) => {
    const rowsToRenderObject = {};
    const studentRowsByGrades = [];

    resultsByGrades.forEach(gradeResult => {
        studentRowsByGrades.push(
            ...buildRowsForGrade({
                gradeResult,
                selectedPmPeriod,
                location,
                handleSaveStudentGoal,
                goalSetLookerEvent,
                isDemoModeOn,
                gradeToMeasureMapper,
                appSpecificParams,
            }),
        );
    });
    rowsToRenderObject.rows = [
        [
            {
                isGridHeader: true,
                isSubheader: true,
                rowObject: {},
            },
        ],
        ...studentRowsByGrades,
    ];
    rowsToRenderObject.colSpans = [
        getProgressGridHeader(
            FIRST_GRID_ROW,
            START_FIRST_COL_SPAN_INDEX,
            COUNT_OF_COLUMNS,
            appSpecificParams.tableTitle,
        ),
        ...getDividedColSpan(
            resultsByGrades,
            LAST_SCORES_START_INDEX,
            LAST_SCORES_END_INDEX,
            buildDividedColSpan('Growth Rate', ZOG_SECTIONS),
        ),
    ];
    return rowsToRenderObject;
};
